import React from 'react';
import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { setShowMiniCart, update } from '../../Actions/Cart.action';
import { translate } from '../../Services/translation';
import SideMenu from '../SideMenu/SideMenu';
import OrderRowProductCard from '../OrderRowProductCard/OrderRowProductCard';
import Icon from '../Icon';
import styled, { createGlobalStyle } from 'styled-components';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import {
  alignCenter,
  displayFlex,
  flex1,
  spaceBetween,
} from '../../styling/utils/flex';
import { StyledSideMenuContent } from '../SideMenu/styles/SideMenu.styling';
import Button from '../Button';
import { breakpoint } from '../../styling/mixins/breakpoints';
import { getColor } from '../../styling/mixins/colors';
import { fontFamilyBold, text } from '../../styling/mixins/typography';
import EditorString from '../EditorString/EditorString';
import ExpressCheckoutButton from '../ExpressCheckout/ExpressCheckoutButton';
import { initExpressCheckoutFromCart } from '../../utils/vippsUtils';
import SuggestedProductsSlider from '../SuggestedProducts/MiniCart/SuggestedProductSlider.MiniCart';

interface StyledMiniCartSideMenuProps {
  $empty: boolean;
  $hasSuggestedProducts: boolean;
}

const MiniCart = () => {
  const dispatch = useDispatch();

  const showMiniCart = useTypedSelector((state) => state.cart.showMiniCart);
  const orderRows = useTypedSelector((state) => state.cart.orderRows);
  const emptyCartLink = useTypedSelector((state) => state.cart.emptyCartLink);
  const emptyCartText = useTypedSelector((state) => state.cart.emptyCartText);
  const emptyCartUsps = useTypedSelector((state) => state.cart.emptyCartUsps);
  const orderTotal = useTypedSelector((state) => state.cart.orderTotal);
  const checkoutUrl = useTypedSelector((state) => state.cart.checkoutUrl);
  const errors = useTypedSelector((state) => state.cart.errors);
  const expressCheckoutEnabled = useTypedSelector((state) => state.cart.expressCheckoutEnabled);
  const suggestedProducts = useTypedSelector((state) => state.suggestedProducts)

  const hasOrderRows = useMemo(() => orderRows?.length > 0, [orderRows]);
  const hasSuggestedProducts = useMemo(() => suggestedProducts.products.length > 0, [suggestedProducts]);

  const userInfo = useTypedSelector((state) => state.userInfo);
  const isBusinessCustomer = userInfo?.current?.isBusinessCustomer;
  const quantity = useMemo(
    () => orderRows.reduce((prev, current) => prev + current.quantity, 0),
    [orderRows]
  );

  const closeMiniCart = () => dispatch(setShowMiniCart(false));
  const Header = () => (
    <StyledMiniCartHeader>
      <h4>
        {translate('general.cart')} {quantity > 0 ? `(${quantity})` : ''}
      </h4>
      <button
        className="close"
        onClick={closeMiniCart}
        aria-label={translate('general.close')}
      >
        <Icon name="close" />
      </button>
    </StyledMiniCartHeader>
  );

  const Footer = hasOrderRows
    ? () => (
      <StyledMiniCartFooter>
        <div className="rows">
          <h5>{translate('general.total')}</h5>
          <h5>
            {orderTotal}{' '}
            {isBusinessCustomer && (
              <span className="vat-text">
                {translate('cart.price-without-vat')}
              </span>
            )}
          </h5>
        </div>
        <a href={checkoutUrl}>
          <Button
            btnType="primary"
            btnSize="large"
            fullwidth={true}
            className="checkout-button"
          >
            {translate('cart.to-checkout')}
          </Button>
        </a>
        {expressCheckoutEnabled && (
          <ExpressCheckoutButton
            onClick={expressCheckoutOnClick}
            variant='checkout'
          />
        )}
      </StyledMiniCartFooter>
    )
    : undefined;


  const removeRow = (row) => {
    dispatch(update(row.articleNumber, 0));
  };

  function onQuantityChange(
    quantity: number,
    articleNumber: string,
    abortController: AbortController
  ) {
    dispatch(update(articleNumber, quantity, abortController))
      .catch((err) => {
        if (err.name !== 'AbortError') {
          console.error('API error:', err);
        }
      });
  }

  const orderRowCards = useMemo(() => {
    return (
      hasOrderRows ? (
        orderRows.map((row) => (
          <OrderRowProductCard
            key={row.rowSystemId + 'mini-cart'}
            row={row}
            onRemoveClick={removeRow}
            onQuantityChange={onQuantityChange}
          />
        ))
      ) : (
        <StyledEmptyCart>
          <div className="empty-cart-text">
            <EditorString value={emptyCartText} />
          </div>
          <StyledCartUsps>
            {emptyCartUsps &&
              emptyCartUsps.map((usp) => (
                <StyledUspItem key={usp.title}>
                  <img
                    alt={usp.alt ?? usp.title}
                    width={32}
                    height={32}
                    src={usp.icon}
                    loading="lazy"
                  />
                  <div className="text-content">
                    {usp.title && <div className="usptitle">{usp.title}</div>}
                    {usp.description && <EditorString value={usp.description.value} />}
                  </div>
                </StyledUspItem>
              ))}
          </StyledCartUsps>
          {emptyCartLink && (
            <StyledEmptyCartLink href={emptyCartLink}>
              <Button
                btnType="outlined"
                btnSize="large"
                className="shopping-button"
              >
                {translate('cart.empty-cart-link')}
              </Button>
            </StyledEmptyCartLink>
          )}
        </StyledEmptyCart>
      )
    );
  }, [hasOrderRows, orderRows, removeRow]);

  const suggestedProductSlider = useMemo(() => {
    return hasSuggestedProducts && hasOrderRows && (
      <SuggestedProductsSlider
        suggestedProducts={suggestedProducts.products}
        sliderIntervalInSec={suggestedProducts.sliderIntervalInSec}
      />
    )
  }, [suggestedProducts, hasOrderRows])

  const memoizedErrors = useMemo(() =>
    errors && errors['general'] && errors['general'].length > 0 && (
      <p className="stock-error">{errors['general'][0]}</p>
    ), [errors])


  const Content = () => (
    <StyledMiniCartContent $empty={!hasOrderRows} $hasSuggestedProducts={hasSuggestedProducts}>
      {memoizedErrors}
      {orderRowCards}
      {suggestedProductSlider}
    </StyledMiniCartContent>
  );

  return (
    <>
      <StyledMiniCartSideMenu
        id="mini-cart"
        open={showMiniCart}
        onClosed={closeMiniCart}
        components={{ Header, Footer, Content }}
        showCloseButton={false}
      ></StyledMiniCartSideMenu>
      <GlobalMiniCartStyling />
    </>
  );
};

export default MiniCart;

async function expressCheckoutOnClick(): Promise<string> {
  try {
    return await initExpressCheckoutFromCart();
  } catch (error) {
    return translate('expresscheckout.general.error');
  }
}

const StyledMiniCartSideMenu = styled(SideMenu)`
  z-index: 62;
  overflow-y: auto;
  -ms-overflow-style: none;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }

  ${breakpoint('small')} {
    border-left: 1px solid ${getColor('grey', 300)};
  }
`;

const StyledCartUsps = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: 2.4rem;
`;

const StyledUspItem = styled.div`
  display: flex;
  align-items: center;
  &:not(:last-of-type) {
    margin-bottom: 1.2rem;
  }
  img {
    margin-right: 0.8rem;
  }
  .usptitle {
    ${fontFamilyBold}
    margin-bottom: 0.2rem;
  }
  .text-content {
    ${text('small')}
    display: flex;
    flex: 1;
    flex-direction: column;
    ${breakpoint('medium')} {
      ${text('medium')}
    }
  }
`;

const StyledMiniCartHeader = styled.div`
  ${displayFlex}
  ${spaceBetween}
  ${alignCenter}
  ${flex1}
  background-color: ${getColor('white')};

  h4 {
    text-transform: uppercase;
    ${fontFamilyBold};
  }

  .close {
    cursor: pointer;
    font-size: 2.5rem;
  }
`;

const StyledMiniCartFooter = styled.div`
  ${flex1}

  .rows {
    ${displayFlex}
    ${spaceBetween}
    ${alignCenter}
    margin-bottom: 1.6rem;
    h5 {
      text-transform: uppercase;
      ${fontFamilyBold};
    }
  }
  .vat-text {
    ${text('medium')};
    color: ${getColor('gray', 600)};
    text-transform: none;
    &:before {
      content: '(';
    }
    &:after {
      content: ')';
    }
  }

  .checkout-button {
    ${fontFamilyBold}
    text-transform: uppercase;
    background-color: ${getColor('primary')};
    border: none;
  }
`;
const StyledEmptyCartLink = styled.a`
  width: 100%;
`;
const StyledEmptyCart = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  .empty-cart-text {
    margin-bottom: 2.4rem;
  }

  .title {
    margin: 1.6rem 0;
  }

  .cart-icon {
    font-size: 7.2rem;
  }

  .shopping-button {
    ${fontFamilyBold};
    width: 100%;
    text-transform: uppercase;
    background-color: ${getColor('black')};
    border-color: ${getColor('black')};
    color: ${getColor('white')};
  }
`;

const StyledMiniCartContent = styled(
  StyledSideMenuContent
) <StyledMiniCartSideMenuProps>`
  background-color: ${getColor('grey', 100)};
  display: flex;
  flex-direction: column;
  gap: 0;
  flex: 1;
  overflow-y: auto;
  padding: ${(p) => (p.$empty ? '2.5rem' : '0 0 0.4rem 0 !important')};

  ${(p) =>
    (!p.$empty && p.$hasSuggestedProducts) &&
    `& > :last-child {
      margin-top: auto;
      margin-bottom: 8px;
  }`}

  .stock-error {
    color: ${getColor('alert')};
    ${text('small')};
    padding-left: 1.6rem;
  }
`;

const GlobalMiniCartStyling = createGlobalStyle`
  body #overlay-container .overlay--mini-cart {
    z-index: 55;
  }
`;